import React, { useEffect, useState, useRef } from 'react';
import apiClient from '../../../api/client';

interface ThreeDTabProps {
  smiles?: string;
}

const ThreeDTab: React.FC<ThreeDTabProps> = ({ smiles }) => {
  const [molblock, setMolblock] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const viewerRef = useRef<HTMLDivElement>(null);

  // Generate 3D coordinates via backend
  useEffect(() => {
    if (!smiles) return;
    setLoading(true);
    setError(null);
    apiClient.post('/v2/indigo/convert', {
      structure: { value: smiles, type: 'RAW' },
      output_format: 'molfile',
    })
      .then((resp) => {
        const raw = resp.data?.struct || resp.data?.result || resp.data;
        const mol = typeof raw === 'string' ? raw : '';
        if (!mol) {
          setError('No 3D data available for this molecule');
          return;
        }
        setMolblock(mol);
      })
      .catch(() => {
        setError('Could not generate 3D coordinates');
      })
      .finally(() => setLoading(false));
  }, [smiles]);

  // Initialize 3Dmol viewer when molblock is available
  useEffect(() => {
    if (!molblock || !viewerRef.current) return;
    const $3Dmol = (window as any).$3Dmol;
    if (!$3Dmol) return;
    const viewer = $3Dmol.createViewer(viewerRef.current, {
      backgroundColor: '#1e293b',
    });
    try {
      viewer.addModel(molblock, 'mol');
      viewer.setStyle({}, { stick: { colorscheme: 'Jmol', radius: 0.15 }, sphere: { scale: 0.25, colorscheme: 'Jmol' } });
      viewer.zoomTo();
      viewer.render();
    } catch (e) {
      setError('Could not render 3D structure');
    }
    return () => viewer.clear();
  }, [molblock]);

  if (!smiles) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-3">
        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>No molecule selected</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-5 h-5 border-2 border-t-transparent border-teal-500 rounded-full animate-spin" />
        <span className="ml-2 text-sm" style={{ color: 'var(--text-muted)' }}>Generating 3D structure...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-3">
        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>{error}</p>
      </div>
    );
  }

  // Check if 3Dmol is loaded
  const has3Dmol = typeof (window as any).$3Dmol !== 'undefined';

  if (!has3Dmol) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-3">
        <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--hover-bg)' }}>
          <svg className="w-6 h-6" style={{ color: 'var(--text-secondary)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
              d="M21 7.5l-9-5.25L3 7.5m18 0l-9 5.25m9-5.25v9l-9 5.25M3 7.5l9 5.25M3 7.5v9l9 5.25m0-9v9"
            />
          </svg>
        </div>
        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>3D viewer library not loaded</p>
        <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Conformer generation, protein-ligand docking poses</p>
      </div>
    );
  }

  return (
    <div
      ref={viewerRef}
      style={{ width: '100%', height: 400, position: 'relative', border: '1px solid var(--card-border)' }}
      className="rounded-lg overflow-hidden"
    />
  );
};

export default ThreeDTab;
